import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { modalityOptions } from '../../../../common/constants/appConstants'
import { getProviderDetailsAddresses } from '../../../../providers/data/providerSelectors'

/**
 * Builds the modality radio's `{ enum, enumNames }` from the static modality constants plus the
 * provider's saved in-person addresses. Each enum value is a JSON string (`'{"type":"video"}'`,
 * or `'{"type":"in_person","state":...,"zipcode":...}'`) that `getBCCharge` later parses.
 *
 * Computed synchronously (useMemo, not useState/useEffect) so the options exist on the first
 * render. The DA Flexible Note renders this metadata through `NoteContainer`, which freezes the
 * note in a ref on mount — options populated after that first render would never be picked up,
 * leaving the modality radio empty and dropping modality from the charge.
 */
export const useModalityOptions = () => {
  const providerAddresses = useSelector(getProviderDetailsAddresses)

  return useMemo(() => {
    const enumValues: string[] = []
    const enumNames: string[] = []

    if (
      Object.prototype.hasOwnProperty.call(modalityOptions, 'in_person') &&
      providerAddresses &&
      providerAddresses.length >= 1
    ) {
      providerAddresses.forEach((providerAddress) => {
        const displayText = `${modalityOptions.in_person}${
          providerAddress?.street1 ? ' - ' + providerAddress.street1 : ''
        }`
        enumValues.push(
          JSON.stringify({
            type: 'in_person',
            state: providerAddress.state,
            zipcode: providerAddress.zipcode,
            street1: providerAddress.street1,
          }),
        )
        enumNames.push(displayText)
      })
    }

    Object.entries(modalityOptions).forEach(([key, label]) => {
      if (key === 'in_person' && providerAddresses && providerAddresses.length >= 1) {
        return
      }
      enumValues.push(JSON.stringify({ type: key }))
      enumNames.push(label)
    })

    return { enum: enumValues, enumNames }
  }, [providerAddresses])
}
